/****************************************************************************************************************
* ©2019 EdgeVerve Systems Limited, Bangalore, India. All Rights Reserved.
*
* The EdgeVerve proprietary software program ("Program"), is protected by copyrights laws, international treaties and
* other pending or existing intellectual property rights in India, the United States and other countries. The Program
* may contain / reference third party or open source components, the rights to which continue to remain with the
* applicable third party licensors or the open source community as the case may be and nothing here transfers the
* rights to the third party and open source components, except as expressly permitted. Any unauthorized reproduction,
* storage, transmission in any form or by any means (including without limitation to electronic, mechanical, printing,
* photocopying, recording or otherwise), or any distribution of this Program, or any portion of it, may result in
* severe civil and criminal penalties, and will be prosecuted to the maximum extent possible under the law.
*****************************************************************************************************************/
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { Constants } from '../../ba-supplier-ui.constants';
import { SupplierAction } from '../../models/SupplierAction';
import { SupplierHeaderData } from '../../models/SupplierHeader';
import { CommonService } from '../../services/common.service';
import { DataService } from '../../services/data.service';
import { HierarchyService } from '../../services/hierarchy.service';
import { SuppUserService } from '../../services/supp-user.service';
import { SuppHierarchyComponent } from '../supp-hierarchy/supp-hierarchy.component';
import { SuppHomeComponent } from '../supp-home/supp-home.component';
import { SuppManageGroupComponent } from '../supp-manage-group/supp-manage-group.component';
import { SuppManageRoleComponent } from '../supp-manage-role/supp-manage-role.component';
import { ProfileService } from '../../../core/services/profile.service';

import {CommonService as _commonService} from '../../../shared/services/common.service'

@Component({
    selector: 'app-supp-users-list',
    templateUrl: './supp-users-list.component.html',
    styleUrls: ['./supp-users-list.component.scss'],
    standalone: false
})
export class SuppUsersListComponent implements OnInit {
  heading = 'supplier.userlist.userManagement';
  addUser = 'supplier.userlist.addUser';
  enableCreateUser = 'SupplierApi_createSupplierUser';

  displayedColumns = [
    'firstName',
    'lastName',
    'userCode',
    'email',
    'phoneNumber',
    // 'roles',
    'actions'
  ];
  dataSource;
  pageSize = 10;
  pageIndex = 0;
  dataSourceLength = 0;
  pageSizeOptions = [10, 25, 50];
  headerData: SupplierHeaderData;
  displayMapHierarchyAction = false;
  searchText = '';
  constructor(
    private userService: SuppUserService,
    private router: Router,
    private data: DataService,
    private commonService: CommonService,
    public homeComponent: SuppHomeComponent,
    private translate: TranslateService,
    public dialog: MatDialog,
    private hierarchyService: HierarchyService,
    private profileService: ProfileService,
    private _commonService: _commonService

  ) {
    this.headerData = new SupplierHeaderData();
    this.translate.get(this.heading).subscribe((result) =>
      this.headerData.title = result
    );
    const searchAction = new SupplierAction();
    searchAction.action = 'search';
    searchAction.title = 'search';
    this.headerData.searchAction = searchAction;

    if(this.profileService.isAuthorized(this.enableCreateUser)){
      this.translate.get(this.addUser).subscribe((result) => {
        const createUserAction: SupplierAction = new SupplierAction();
        createUserAction.title = result;
        createUserAction.action = 'submit';
        createUserAction.color = 'primary';
        this.headerData.actions.push(createUserAction);
      });
    } 
    this.hierarchyService.getData().subscribe(response => {
      this.data.defaultHierarchy = response;
      if (this.data.defaultHierarchy.length > 0) {
        this.displayMapHierarchyAction = true;
      }
    });
  }

  ngOnInit() {
    this.getUsers(null);
  }
  headerAction(action: SupplierAction) {
    if (action.action === 'submit') {
      this.createUser();
    } else if (action.action === 'search') {
      this.searchText = action.data;
      this.pageIndex = 0;
      this.loadData(this.searchText, this.pageIndex, this.pageSize);
    }
  }

  getUsers(data, searchText = '') {
    const self = this;
    if (!data) {
      this.pageIndex = 0;
      data = {
        skip: this.pageIndex, limit: this.pageSize
      };
    }
    if (searchText) {
      data['searchText'] = searchText;
    }
    this.homeComponent.isAppBusy = true;
    this.userService.getUsers(data).subscribe(response => {
      if (response && response.data) {
        self.dataSource = response.data.map(user => {
          return {
            id: user.id,
            firstName: user.firstName ? user.firstName : '',
            lastName: user.lastName ? user.lastName : '',
            email: user.email,
            phoneNumber: user.phoneNumber ? user.phoneNumber : '',
            userCode: user.userCode,
            roles: user.roles,
            defaultLocationId: user.defaultLocationId
          };
        });
        self.dataSourceLength = response.numFound;
        self.homeComponent.isAppBusy = false;
      } else {
        self.dataSourceLength = 0;
        self.homeComponent.isAppBusy = false;
      }
    },
      (err) => {
        self.dataSourceLength = 0;
        self.homeComponent.isAppBusy = false;
      });
  }

  setPageSize(event): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadData(this.searchText, event.pageIndex, event.pageSize);
  }

  loadData(searchText = '', pageNo = 0, maxSize = this.pageSize) {
    const data = {
      searchText: searchText,
      skip: pageNo * maxSize,
      limit: maxSize
    };
    this.getUsers(data);
  }

  createUser() {
    this.router.navigate(['sup/create-user']);
  }

  editUser(userData) {
    this.data.suppuser = userData;
    this.router.navigate(['sup/create-user', userData.id]);
  }

  async deleteUser(userData) {
    const self = this;
    const data = { id: userData.id, email: userData.email };

    const title: any = await this._commonService.translate('supplier.userlist.dialog.deleteUser.title');
    const content: any = await this._commonService.translate('supplier.userlist.dialog.deleteUser.content');
    this.commonService.openDialog(title, content, 'confirm', function (result) {
      if (!result) {
        return;
      }
      self.userService
        .deleteUser(data)
        .toPromise()
        .then(async response => {
          if (response.errorMessage) {
            self.commonService.openSnackBar(response.errorMessage);
          } else {
            self.commonService.openSnackBar(              
              await this._commonService.translate('supplier.userlist.dialog.deleteUser.success',userData)
            );
            self.getUsers({ skip: self.pageIndex, limit: self.pageSize });
          }
        })
        .catch(error => {
          self.commonService.openSnackBar(error.message);
        });
    });
  }

  mapUserToHierarchy(user: any) {
    this.data.suppuser = user;
    const dialogRef = this.dialog.open(SuppHierarchyComponent, {
      width: '1000px',
      height: '500px'
    });
  }

  mapUserToGroup(user: any) {
    this.userService.getUserRoles(user.id).toPromise()
      .then(response => {
        user.roleId = response.id;
        this.dialog.open(SuppManageGroupComponent, {
          width: '600px',
          height: '300px',
          data: {
            user: user
          }
        });
      })
      .catch(error => {
        //TODO: Read error code and display proper error message
        this.commonService.openSnackBar('Error: ' + error.message);
      });
  }

  assignRoles(user) {
    this.homeComponent.isAppBusy = true;
    if (user && user.id) {            
      this.userService.getRoleCodes().subscribe(roleCodeRes=> {
        let userRole = {};
        this.userService.getAppList().subscribe(appListRes => {
          this.userService.getUserRoles(user.id) .subscribe(userRoleRes=> {
            if(!userRoleRes){
              userRoleRes = {};       
            }
            const apps = userRoleRes.apps.map(x => {
              let appName = appListRes.filter(i=> i.appCode==x)[0].appName;
                return { 'id': x, 'itemName': appName + "("+x+")" }});
              const roles = userRoleRes.roles.map(x => ({ 'id': x, 'itemName': x }));
              userRoleRes.apps = apps;
              userRoleRes.roles = roles;
              userRole = userRoleRes;
            this.homeComponent.isAppBusy = false;
            this.dialog.open(SuppManageRoleComponent, {
              width: Constants.DIALOG_WIDTH_85,
              data: {
                userRole: userRole,
                roleCodes: roleCodeRes ? roleCodeRes.map(x => ({ 'id': x, 'itemName': x })) : [],
                applist: appListRes ? appListRes.map(x => ({ 'id': x.appCode, 'itemName': x.appName +" ("+x.appCode+")"})) : [],
              }
            });
          }, err=> {
            this.homeComponent.isAppBusy = false;
            this.userService.openSnackBar("Unable to fetch user roles and apps "+ err);
          })
        },
        err=> {
          this.homeComponent.isAppBusy = false;
          this.userService.openSnackBar("Unable to fetch roles list "+ err);
        })
      },
      err=> {
        this.homeComponent.isAppBusy = false;
        this.userService.openSnackBar("Unable to fetch apps list "+ err);
      });      
    }
    
  }
}


